/**
 * Brand color palette — JS mirror of the CSS tokens in src/index.css.
 *
 * Use this only when colors must be passed to JS APIs (chart libs,
 * inline FontAwesome `color` props, dynamic style objects). For
 * everything else, prefer CSS variables on classes.
 *
 * CSS is the source of truth. If a literal here drifts from its
 * counterpart in src/index.css, fix the CSS first and mirror back.
 * Dark-mode variants are intentionally NOT mirrored — read
 * getComputedStyle(document.documentElement).getPropertyValue('--token')
 * if you need mode-aware values at runtime.
 */

// ===== Brand =====
export const BRAND = {
  navy:        '#0f2235',
  navyDark:    '#081523',
  navyLight:   '#1a3553',
  brass:       '#b8893f',
  brassDark:   '#8d6829',
  brassLight:  '#d8b074',
  oxblood:     '#7a2e2e',
  oxbloodDark: '#5c1f1f',
};

// ===== Ink scale =====
export const INK = {
  primary:   '#1c1611',
  secondary: '#4a4338',
  muted:     '#6b6457',
  inverse:   '#f5ecd9',
};

// ===== Surfaces =====
export const SURFACE = {
  card:          '#ffffff',
  parchment:     '#f5ecd9',
  parchmentSoft: '#faf5e8',
};

// ===== Status (semantic) =====
export const STATUS = {
  success: '#4f7a4a',     // sage
  warning: BRAND.brass,
  danger:  BRAND.oxblood,
  info:    BRAND.navyLight,
  muted:   INK.muted,
};

// Light parchment-tinted washes that pair with each STATUS color.
export const STATUS_BG = {
  success: '#e3eddf',
  warning: '#f5e6c8',
  danger:  '#f3dada',
  info:    '#d6e0ee',
  muted:   '#ece8df',
};

// 4-band severity palette (admin error monitor, alert tables).
export const SEVERITY = {
  critical: { color: BRAND.oxblood,     bgColor: STATUS_BG.danger  },
  high:     { color: BRAND.oxbloodDark, bgColor: STATUS_BG.danger  },
  medium:   { color: BRAND.brass,       bgColor: STATUS_BG.warning },
  low:      { color: STATUS.success,    bgColor: STATUS_BG.success },
};

// Health status palette — SystemHealth, AdminDashboard.
export const HEALTH = {
  healthy:  STATUS.success,
  warning:  STATUS.warning,
  critical: STATUS.danger,
  error:    STATUS.danger,
  poor:     STATUS.danger,
  disabled: STATUS.muted,
  checking: STATUS.muted,
  unknown:  STATUS.muted,
};

// Performance band palette (good / acceptable / slow).
export const PERFORMANCE = {
  good:               STATUS.success,
  acceptable:         STATUS.warning,
  'needs-improvement':STATUS.warning,
  slow:               STATUS.danger,
  poor:               STATUS.danger,
};

// Categorical palette for charts and per-category icons. 7 hues drawn
// from brand tokens so charts stay on-theme. Loop if more are needed.
export const CHART_PALETTE = [
  BRAND.navy,
  BRAND.brass,
  STATUS.success,
  BRAND.oxblood,
  BRAND.navyLight,
  BRAND.brassDark,
  '#6e5e4a', // taupe — a single off-palette hue to preserve distinguishability
];

// Avatar background palette used by getColorFromName() in OptimizedImage.
export const AVATAR_PALETTE = [
  BRAND.navy,
  BRAND.brass,
  BRAND.brassDark,
  BRAND.oxblood,
  BRAND.navyLight,
  STATUS.success,
  '#6e5e4a',
  '#8a6f4a',
];
